import { useEffect, useRef, useState } from "react";
import type { RepositoryView } from "@baton/contracts";
import { buildMessageContent, emptyHandoffDraft, type HandoffDraftState } from "./compose.js";
import { Modal } from "./components/modal.js";
import type { AgentRef } from "./flow/nodes.js";
import { HandoffFields } from "./handoff-fields.js";
import type { SendDraft } from "./hooks/use-project-messages.js";

/**
 * Opens right after two agents are wired: send a message or a handoff along the new
 * connection. The dashboard always sends as the viewer's general agent; the agent the
 * wire started from can be delivered a copy so both ends learn about each other.
 */
export function WireComposer(props: {
  open: boolean;
  source: AgentRef | null;
  target: AgentRef | null;
  viewerGeneralAgentId: string | null;
  sessionReady: boolean;
  /** The selected project, named in the handover so the receiving agent knows where it is. */
  project: { id: string; name: string } | null;
  /** The project's repositories: the handoff can point at one of them. */
  repositories: RepositoryView[];
  onSend(draft: SendDraft): Promise<void>;
  onSkip(): void;
  onClose(): void;
}) {
  const [type, setType] = useState<"chat" | "handoff">("chat");
  const [body, setBody] = useState("");
  const [handoff, setHandoff] = useState<HandoffDraftState>(() => emptyHandoffDraft());
  const [notifySource, setNotifySource] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Read through a ref on open so a project refresh while typing never resets the form.
  const repositoriesRef = useRef(props.repositories);
  repositoriesRef.current = props.repositories;

  useEffect(() => {
    if (!props.open) return;
    setType("chat"); setBody(""); setHandoff(emptyHandoffDraft(repositoriesRef.current));
    setNotifySource(true); setError(null); setWorking(false);
  }, [props.open, props.source?.agentId, props.target?.agentId]);

  const source = props.source;
  const target = props.target;
  const sourceIsMe = source?.agentId === props.viewerGeneralAgentId;
  const targetIsMe = target?.agentId === props.viewerGeneralAgentId;

  async function send(event: React.FormEvent) {
    event.preventDefault();
    if (!source || !target || !body.trim()) return;
    setWorking(true); setError(null);
    try {
      const repository = type === "handoff"
        ? props.repositories.find((candidate) => candidate.id === handoff.repositoryId) ?? null
        : null;
      const content = buildMessageContent({ type, body, handoff, project: props.project, repository });
      const recipients = [target.agentId];
      if (notifySource && !sourceIsMe && !targetIsMe) recipients.push(source.agentId);
      await props.onSend({
        recipientAgentIds: recipients.filter((id) => id !== props.viewerGeneralAgentId),
        type,
        content,
        repoId: repository?.id ?? null,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not send");
      setWorking(false);
    }
  }

  const bodyField = <label>{type === "handoff" ? "Brief" : "Message"}
    <textarea value={body} onChange={(event) => setBody(event.target.value)} rows={4} required placeholder={type === "handoff" ? "What the agent should do, and where." : `Say something to ${target?.handle ?? "the agent"}…`} />
  </label>;

  return <Modal open={props.open && source !== null && target !== null} onClose={props.onClose} title="Agents connected" size={type === "handoff" ? "wide" : "regular"}>
    {source && target && <form className="wire-composer" onSubmit={send} data-testid="wire-composer">
      <p className="wire-composer-pair">
        <strong>{source.handle}</strong> <span aria-hidden="true">⟶</span> <strong>{target.handle}</strong>
      </p>
      <p className="muted">These two agents can now talk to each other. Start the conversation: a quick message, or a handoff with acceptance criteria and a handover that {target.handle} works through.</p>
      <div className="tabs" role="tablist" aria-label="What to send">
        <button type="button" role="tab" aria-selected={type === "chat"} className={type === "chat" ? "active" : ""} onClick={() => setType("chat")}>Message</button>
        <button type="button" role="tab" aria-selected={type === "handoff"} className={type === "handoff" ? "active" : ""} onClick={() => setType("handoff")}>Handoff</button>
      </div>
      {type === "handoff"
        ? <HandoffFields value={handoff} onChange={setHandoff} repositories={props.repositories} showRepositoryPicker>{bodyField}</HandoffFields>
        : bodyField}
      {!sourceIsMe && !targetIsMe && <label className="check-row wire-composer-notify">
        <input type="checkbox" checked={notifySource} onChange={(event) => setNotifySource(event.target.checked)} />
        Also deliver to {source.handle}, so both agents see it
      </label>}
      <p className="muted compact">{props.sessionReady ? "Sent as your general agent, encrypted in this page." : "Preparing your dashboard session…"}</p>
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="secondary" onClick={props.onSkip} disabled={working}>Wire only</button>
        <button type="submit" className="primary" disabled={working || !body.trim() || !props.sessionReady}>{working ? "Sending…" : type === "handoff" ? "Send handoff" : "Send message"}</button>
      </div>
    </form>}
  </Modal>;
}
